<template>
	<div class="pos-terminals-block">
		<div class="header">
			<h3
				@click="toggleCollapsibleComponent('posTerminals')"
				@keydown.enter="toggleCollapsibleComponent('posTerminals')"
				class="collapse-header gray-50"
			>
				POS Термінали
				<div v-if="posList && posList.length" class="count">
					{{ posList ? posList.length : 0 }}
				</div>
				<i :class="collapsedComponents.includes('posTerminals') ? 'desc' : 'asc'" />
			</h3>
			<div v-if="isCashdeskOpened" class="balance">
				<span class="text">Баланс, картка</span>
				<span class="amount">
					<span class="uah" />{{ root.cashdesk && root.cashdesk.card_in_balance | formatMoney }}
				</span>
			</div>
		</div>
		<div v-if="!collapsedComponents.includes('posTerminals') && posList.length">
			<NP-Table class="pos-terminals-table" :data="posList" :columns="columns">
				<template #rowActions="{ slotProps: { row } }">
					<div class="buttons">
						<NP-Button class="details-btn" @click.stop="() => togglePosDetailsModal(row)">Деталі</NP-Button>
						<NP-Button
							class="refresh-btn"
							variant="square"
							@click.stop="() => refreshPosInfo({ code: row.code, loadingAction: { id: row.id } })"
							:loading="loadingActions.includes(`refreshPosInfo-${row.id}`)"
						>
							<div slot="icon" class="icon-refresh" />
						</NP-Button>
					</div>
				</template>
				<template #pos_code="{ slotProps: { row } }">
					{{ row.code }}
					<div v-if="showDisabledRowMessage(row)" class="disabled-row-message">
						Для можливості оплати на данному ПОС терміналі створіть заявку на СД
					</div>
				</template>
			</NP-Table>
		</div>
		<POSDetailsModal
			v-if="state_key.render === 'pos_details_modal'"
			:last-receipt-details="lastReceiptDetails"
			:get-pos-transactions="getPosTransactions"
			:pos-details="posDetails"
			:pos-transactions-details="posTransactionsDetails"
			:loading-actions="loadingActions"
			:toggle="togglePosDetailsModal"
			:get-last-receipt="getLastReceipt"
		/>
	</div>
</template>

<script>
import orderBy from 'lodash/orderBy';
import { mapGetters } from 'vuex';

import POSDetailsModal from './pos-details-modal.vue';

import { enum as posStatuses } from '@repo/enums/pos-terminal-statuses';

export default {
	name: 'POSTerminals',
	components: { POSDetailsModal },
	props: {
		root: { type: Object, default: () => ({}) },
		posDetails: { type: Object, default: () => ({}) },
		lastReceiptDetails: { type: Object, default: () => ({}) },
		posTransactionsDetails: { type: Object, default: () => ({}) },
		state_key: { type: [Object, String], default: null },
		collapsedComponents: { type: Array, default: () => [] },
		loadingActions: { type: Array, default: () => [] },
		toggleCollapsibleComponent: { type: Function, default: null },
		getLastReceipt: { type: Function, default: null },
		togglePosDetailsModal: { type: Function, default: null },
		getPosTransactions: { type: Function, default: null },
		refreshPosInfo: { type: Function, default: null }
	},
	computed: {
		...mapGetters({ isCashdeskOpened: 'root/isCashdeskOpened' }),
		...mapGetters('root', ['isPosDaemonOutdated']),
		posList() {
			return this.root.posTerminals && this.root.posTerminals.length
				? orderBy(
						this.root.posTerminals.filter((d) => !!d.code && d.status === posStatuses.active),
						['code', 'desc']
				  )
				: [];
		},
		columns() {
			return [
				{
					key: 'code',
					label: '№ POS Терміналу',
					type: 'slot',
					slotName: 'pos_code'
				},
				{
					type: 'list',
					key: 'merchant_bank',
					label: 'Банк',
					class: 'wide',
					list: this.root.lists['pos-merchant-banks']
				},
				{
					type: 'amount',
					key: 'last_known_debit_balance',
					label: 'Дебет'
				},
				{
					type: 'amount',
					key: 'last_known_credit_balance',
					label: 'Кредит'
				},
				{
					type: 'amount',
					key: 'last_known_cancelled_balance',
					label: 'Відхилено'
				},
				{
					type: 'slot',
					slotName: 'rowActions',
					class: 'row-actions'
				}
			];
		}
	},
	methods: {
		showDisabledRowMessage(row) {
			return (
				(this.isPosDaemonOutdated && row.merchant_bank === 'tas') ||
				(row.merchant_bank === 'aval' && row.code.startsWith('000'))
			);
		}
	}
};
</script>

<style lang="scss" scoped>
@import '@assets/scss/mixins';
@import '@assets/scss/variables';

$icon-refresh: '~@assets/images/icon-refresh.svg';

.pos-terminals-block {
	width: 100%;
	padding: 20px 24px;
	margin-top: 20px;
	background-color: $white;
	border-radius: $border-radius;
	.header {
		display: flex;
		justify-content: space-between;
		.collapse-header {
			@include collapse-header();
		}
		.balance {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 0 14px;
			width: 450px;
			height: 40px;
			background-color: $gray-5;
			border-radius: $border-radius;
			font-size: 14px;
			line-height: 17px;
			.text {
				font-family: Proxima Nova;
			}
			.amount {
				font-family: Proxima Nova Semibold;
				font-size: 20px;
				.uah {
					margin-right: 4px;
				}
			}
			@include small-desktop() {
				width: 426px;
			}
		}
	}
	.pos-terminals-table {
		.disabled-row-message {
			position: absolute;
			z-index: 1;
			bottom: 8px;
			left: 0;
			width: 490px;
			margin: 0 0 0 10px;
			font-size: 12px;
			line-height: 14px;
			color: $red;
		}
		::v-deep .controls-container {
			display: none;
		}
		::v-deep .table table {
			th {
				padding-bottom: 0;
			}
			tbody tr {
				td {
					background: $gray-10;
					border-top: 6px solid $white;
					border-bottom: 6px solid $white;
					&:first-child {
						border-top-left-radius: 8px;
						border-bottom-left-radius: 8px;
					}
					&:nth-child(5) {
						border-top-right-radius: 8px;
						border-bottom-right-radius: 8px;
					}
					&:nth-child(6) {
						background: $white;
					}
				}
				.cell {
					height: 40px;
					padding: 0 10px;
					.row-actions {
						position: absolute;
						top: 7px;
						right: 20px;
						display: flex;
						justify-content: flex-end;
					}
				}
				.cell.wide {
					width: 300px;
				}
				.cell.row-actions {
					width: 182px;
					padding-right: 0;
					.buttons {
						display: flex;
						flex-wrap: nowrap;
						.refresh-btn {
							margin-left: 8px;
							.icon-refresh {
								@include make-icon($icon-refresh, $gray-50, 20px, 20px);
							}
						}
					}
				}
			}
		}
	}
	@include mobile() {
		display: flex;
		flex-direction: column;
		padding: 16px;
		.header {
			display: flex;
			flex-direction: column;
			.balance {
				margin: 16px 0 0 0;
				width: 100%;
			}
		}
	}
}
</style>
